/**
 * The backend's bridge to the shared meta-game rules (src/game/rules, design D2): the RuleContext of a server op
 * (server time, the player's calendar, the server RNG, the player's server state), running an op with the
 * serverState channel, the mapping of rule failures to protocol errors, and the player's time zone rule.
 *
 * Used by ProfileService (profile.create / session.enter on profile.load) and CommandService (player.command).
 */
import { type JsonValue, cloneJson, isPlainObject } from '../../shared/json';
import type { RandomSource } from '../../shared/random';
import { BackendError } from '../../shared/protocol/errors';
import {
  type GameDefs,
  type OpRegistry,
  type RuleContext,
  type RuleError,
  type StoredGameState,
  createGameDefs,
  createRuleContext,
  executeOp,
  MAX_TZ_OFFSET_MIN,
  MIN_TZ_OFFSET_MIN,
  unitRandom,
} from '../../game/rules/index';
import { type ServerState, type ServerStateContext, withServerState } from '../../game/rules/ops/shop-common';
import type { StoredTimeZone } from './services/deps';

export type { ServerState };

let sharedDefs: GameDefs | null = null;

function deepFreeze<T>(v: T, seen = new Set<object>()): T {
  if (v === null || typeof v !== 'object' || seen.has(v as object)) return v;
  seen.add(v as object);
  for (const k of Object.keys(v as object)) deepFreeze((v as Record<string, unknown>)[k], seen);
  return Object.freeze(v);
}

/**
 * The DEF tables of every server op: built once per process (createGameDefs costs about a millisecond and a few
 * hundred kilobytes) and deep-frozen. Rule ops only read ctx.defs (RuleContext contract; every committed rule fixture
 * replays identically with the frozen tables), so sharing is safe, and a stray write would throw instead of leaking
 * into later commands of other players. The game client keeps building its own mutable copies (FWP.init_data_arrays).
 */
export function serverGameDefs(): GameDefs {
  if (!sharedDefs) sharedDefs = deepFreeze(createGameDefs());
  return sharedDefs;
}

export interface ServerRuleEnv {
  /** Server time, epoch ms (the rules get unix seconds). */
  nowMs: number;
  /** The player's calendar (minutes east of UTC). */
  tzOffsetMin: number;
  /** The server RNG; rule ops draw through it with FWP.true_random's formula. */
  random: RandomSource;
  serverState?: ServerState;
}

/** RuleContext of a server op, with the player's server state attached (withServerState). */
export function serverRuleContext(env: ServerRuleEnv): RuleContext & ServerStateContext {
  const ctx = createRuleContext({
    now: Math.floor(env.nowMs / 1000),
    tzOffsetMin: env.tzOffsetMin,
    rng: unitRandom(env.random),
    defs: serverGameDefs(),
  });
  return withServerState(ctx, env.serverState ?? {});
}

export interface RanOp {
  gameState: StoredGameState;
  /** The op's result without `serverState` (JSON copy). */
  result: JsonValue;
  /** The whole new server state when the op returned one (result.serverState), else null. */
  serverState: ServerState | null;
}

/**
 * executeOp plus the serverState channel: an op that owns server state returns the whole new server state as
 * `result.serverState`; it is taken out of the result (clients never see server state) and returned separately.
 * Throws RuleError for bad arguments, an undecodable state and failed preconditions.
 */
export function runRulesOp(op: string, gameState: unknown, args: unknown, ctx: RuleContext, registry: OpRegistry): RanOp {
  const out = executeOp(op, gameState, args, ctx, registry);
  let result: unknown = out.result;
  let serverState: ServerState | null = null;
  if (isPlainObject(result) && Object.prototype.hasOwnProperty.call(result, 'serverState')) {
    const { serverState: s, ...rest } = result as { serverState?: unknown };
    if (isPlainObject(s)) serverState = cloneJson(s as ServerState);
    result = rest;
  }
  const json = result === undefined ? null : (cloneJson(result) as JsonValue);
  return { gameState: out.gameState, result: json ?? null, serverState };
}

/** A rule failure as the protocol reports it: VALIDATION_FAILED { reason, details }. */
export function ruleFailure(op: string, err: RuleError): BackendError {
  return new BackendError('VALIDATION_FAILED', `${op}: ${err.reason}`, { reason: err.reason, details: cloneJson(err.details) ?? {} });
}

// ------------------------------------------------------------------------------------------------ time zone

/** A stored time zone may change at most once per this interval (anti-abuse of the daily rules). */
export const TZ_CHANGE_INTERVAL_MS = 24 * 3600_000;

export function isValidTzOffset(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= MIN_TZ_OFFSET_MIN && v <= MAX_TZ_OFFSET_MIN;
}

export interface TimeZoneDecision {
  /** The offset the player's calendar uses now. */
  offsetMin: number;
  /** The time zone to store (undefined: none stored yet). */
  stored: StoredTimeZone | undefined;
  changed: boolean;
}

/**
 * The player's time zone rule: the first offset the device reports is stored; a different one replaces it only when
 * the stored one is at least TZ_CHANGE_INTERVAL_MS old (travel, daylight saving time), otherwise the stored offset
 * stays in effect. Without any report the calendar is UTC.
 */
export function decideTimeZone(stored: StoredTimeZone | undefined, requested: number | undefined, nowMs: number): TimeZoneDecision {
  const current = stored && isValidTzOffset(stored.offsetMin) ? stored : undefined;
  if (requested === undefined || !isValidTzOffset(requested)) return { offsetMin: current?.offsetMin ?? 0, stored: current, changed: false };
  if (!current) return { offsetMin: requested, stored: { offsetMin: requested, changedAt: nowMs }, changed: true };
  if (current.offsetMin === requested || nowMs - current.changedAt < TZ_CHANGE_INTERVAL_MS) return { offsetMin: current.offsetMin, stored: current, changed: false };
  return { offsetMin: requested, stored: { offsetMin: requested, changedAt: nowMs }, changed: true };
}
